/**
 * Moves focus to whichever of the given fields comes first on screen. Focus (rather
 * than a bare scroll) also announces the field's label and its `aria-describedby`
 * error text, and puts the caret where the fix is needed.
 *
 * This matters most inside the scrolling form modals, where a field that fails a
 * format check can render its error well outside the visible area.
 *
 * Callers pass the ids of the fields that failed, in any order: the earliest one in
 * document order wins, so a JSX reshuffle can never leave the focus target out of
 * sync with what the user sees.
 */
export const focusFirstInvalidField = (fieldIds: Array<string | false | null | undefined>): void => {
  const fields = fieldIds
    .filter((fieldId): fieldId is string => !!fieldId)
    .map((fieldId) => document.getElementById(fieldId))
    .filter((field): field is HTMLElement => !!field)
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

  if (fields.length === 0) return;

  // A field can refuse focus — the tour form locks its email input for signed-in
  // renters, for example — so fall through to the next failure instead of leaving
  // focus on <body> and the error off screen.
  const focused = fields.find((field) => {
    field.focus({ preventScroll: true });
    return document.activeElement === field;
  });

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  // Optional call: jsdom (and some embedded webviews) do not implement scrollIntoView.
  (focused ?? fields[0])?.scrollIntoView?.({
    block: 'center',
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
};
